import { store } from "src/store";
import moment from "moment";

// Actions
import { wsConnected, wsDisconnected, updateAvailablePas, AppStatus, loadPasStatus, updateLastStatusDate } from "src/store/context/reducer";
import { WsStatusTypes } from "src/types";

// Configs
export let ws: WsInstance;

type WsConfig = {
  queuesArray: Array<string>;
  paName: string;
  paRamal: number;
  paId: string;
  hasMorgaCertificate: boolean;
};

type WsInstance = {
  sendMessage: Function;
  disconnect: Function;
  connect: Function;
  on: Function;
  clearBinds: Function;
};

interface CustomWebSocket extends WebSocket {
  id?: number;
  ping?: Function;
  lastCheck?: number;
}

interface WsInstanceOptions {
  onMessage?: Function;
  onError?: Function;
  onOpen?: Function;
  onClose?: Function;
  isReconnection?: boolean;
}

const getWsInstance = (config: WsConfig, options: WsInstanceOptions): Promise<CustomWebSocket> => {
  return new Promise((resolve, reject) => {
    const wsAddress = import.meta.env.VITE_WS_URL + `?uuid=${config.paId}`;

    // Create instance
    try {
      const websocket: CustomWebSocket = new WebSocket(wsAddress);

      websocket.id = Date.now();
      websocket.ping = () => {
        websocket?.send(JSON.stringify({ type: "PING" }));
      };

      // Bind messages
      websocket.onmessage = async (message) => {
        return options.onMessage && options.onMessage(message);
      };

      // Bind openning
      websocket.onopen = () => {
        options.onOpen && options.onOpen(websocket, options.isReconnection);
        return resolve(websocket);
      };

      // Bind closing
      websocket.onclose = (e) => {
        return options.onClose && options.onClose(e);
      };

      // Bind error
      websocket.onerror = (e) => {
        reject(e);
        return options.onError && options.onError(e);
      };
    } catch (ex) {
      reject(ex);
      return options.onError && options.onError(ex);
    }
  });
};

export async function createWsClient(config: WsConfig): Promise<WsInstance> {
  let websocket: CustomWebSocket;
  let readyCheckTimer: any;

  // Some binds
  const binds: Array<{ ref: number; eventName: string; callback: Function }> = [];

  // => HANDLERS
  const onMessage = async (message: any) => {
    const event = JSON.parse(message.data);
    const bindHandlers = binds.filter((b) => b.eventName === event.type);

    // Call binds
    if (bindHandlers) await Promise.all(bindHandlers.map((b) => b.callback.call(null, event.data)));

    // Log
    if (event.type != "PA_STATUS_UPDATE") console.log(`Websockets: ${event.type}`, event.data);

    switch (event.type) {
      case "PA_STATUS_UPDATE": {
        const fullState = store.getState();

        if (fullState.auth.userInfo.id === event.data.paQueueId) {
          const date = new Date(event.data.statusUpdatedAt).getTime();
          store.dispatch(updateLastStatusDate(date));
        } else {
          store.dispatch(updateAvailablePas(event.data));
        }

        break;
      }

      case "LIST_PAS_STATUS":
        store.dispatch(loadPasStatus(event.data));
        break;

      case "PONG":
        websocket.lastCheck = new Date().getTime();
        break;

      default:
        return false;
    }
  };

  const onOpen = (ws: CustomWebSocket, isReconnection: boolean) => {
    console.log("Websockets: Connection openned.");

    // Update WS
    websocket = ws;

    const {
      config: stateConfig,
      context,
      auth: { userInfo, paused },
      call: { callDetails },
    } = store.getState();

    let event: any;

    const joinQueueEvent = {
      type: "PA_REGISTERED_ON_QUEUES",
      data: {
        queues: stateConfig.queues || ["oculta"],
        paName: config.paName,
        ramal: stateConfig.sip.paNumber || config.paRamal,
        status: "AVAILABLE",
      },
    };

    const needsRestore = context.appStatus === AppStatus.IN_CALL || context.appStatus === AppStatus.IN_PAUSE;

    // Rejoin queue
    if (isReconnection) {
      sendMessage(joinQueueEvent);
    }

    // Restore
    if (needsRestore) {
      event = {
        type: "PA_RESTORE_STATE",
        data: {
          id: userInfo.id,
          paName: userInfo.name,
          queues: stateConfig.queues,
          ramal: stateConfig.sip.paNumber,
          /*   ramalVoip: accessDotNotation(
            self,
            "sipConfigurations.defaultSipConfiguration.ramal"
          )
            ? accessDotNotation(
                self,
                "sipConfigurations.defaultSipConfiguration.ramal"
              )
            : self.pa().ramal, */
        },
      };

      if (context.appStatus === AppStatus.IN_PAUSE) {
        event.data.status = "PAUSED";
        event.data.pauseType = paused?.texto;
      }

      const nome = callDetails?.uda.usuario.nome;
      if (nome) event.data.restoredUdaName = nome;
    } else if (!isReconnection) {
      event = {
        type: "PA_REGISTERED_ON_QUEUES",
        data: {
          queues: stateConfig.queues || ["oculta"],
          paName: config.paName,
          ramal: stateConfig.sip.paNumber || config.paRamal,
          status: "AVAILABLE",
          hasMorgaCertificate: !!config.hasMorgaCertificate,
        },
      };
    }

    sendMessage(event);

    // Update status
    store.dispatch(wsConnected());

    // Check
    websocket.lastCheck = new Date().getTime();
  };

  const onClose = async (e: any) => {
    store.dispatch(wsDisconnected());

    // Check reason
    if (e.code !== 1000) {
      console.log("Websockets: Unexpected closing");

      // Reconnect
      await connect(true);
    }
  };

  const onError = (e: any) => {
    store.dispatch(wsDisconnected());
  };

  // METHODS
  const sendMessage = (message: object) => {
    console.log("Websockets: Message sent", message);

    try {
      const _message = JSON.stringify(message);
      websocket.send(_message);

      return { success: true };
    } catch (error) {
      return {
        success: false,
      };
    }
  };

  const disconnect = () => {
    console.log("Websockets: Normal disconect");

    // Clear interval
    clearInterval(readyCheckTimer);

    // Send close 1000
    websocket.close(1000);
  };

  const clearBinds = (bindsArray: Array<number>) => {
    if (bindsArray)
      return bindsArray.forEach((id) => {
        const index = binds.findIndex((b) => b.ref === id);
        return !!binds.splice(index, 1).length;
      });

    return binds.splice(0, binds.length);
  };

  const on = (eventName: string, callback: Function) => {
    const ref = new Date().getTime();

    // Create remove ref

    // Push list
    binds.push({ ref, eventName, callback });

    return ref;
  };

  const wsOptions = {
    onMessage,
    onOpen,
    onClose,
    onError,
  };

  const connect = async (isReconnection = false) => {
    websocket = await getWsInstance(config, { ...wsOptions, isReconnection });

    // Define TICKTIMER
    if (readyCheckTimer) clearInterval(readyCheckTimer);

    readyCheckTimer = setInterval(async function ping() {
      // console.info(`Websockets: Checking ${websocket.id}`);

      const now = new Date().getTime();

      const state = store.getState();

      if (websocket.lastCheck) {
        if (now - websocket.lastCheck > 8000) {
          console.log(`Websocket: último pong em ${moment(websocket.lastCheck).format("HH:mm:ss")}. Diferença detectada de ${moment(now - websocket.lastCheck).format("ss")} segundos`);

          store.dispatch(wsDisconnected());

          websocket.close(3000);

          clearInterval(readyCheckTimer);

          // websocket = await getWsInstance(config, wsOptions);
        } else {
          if (state.context.wsStatus !== WsStatusTypes.CONNECTED) store.dispatch(wsConnected());

          websocket.lastCheck = now;
        }
      } else {
        websocket.lastCheck = now;
      }

      // Ping
      if (websocket.readyState === 1) websocket.ping && websocket.ping();
    }, 2000);

    return websocket;
  };

  // UPDATE SINGLETON
  await connect();

  ws = { clearBinds, sendMessage, disconnect, connect, on };

  return ws;
}
